import { Container, Graphics } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import type { EntityId, SimEvent, SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import type { EnemyPose } from '../enemies';
import type { Frame } from '../frame';
import { hash } from '../hash';
import { easeOutCubic } from '../motion';
import { byId, hide, keepIn, setTint } from '../util';
import { NEEDLE_TIP } from './moustiques-face';
import { FINE, SIZE, paintMosquitoes } from './moustiques-looks';
import {
  FLOAT,
  FORM_BEATS,
  SHOVE,
  UNIT,
  alive,
  density,
  formSway,
  forming,
  jitter,
  orbit,
  part,
  shove,
  spark,
} from './moustiques-moments';
import {
  type Farewell,
  type Swarm,
  createSwarmPool,
  drawFarewell,
  drawSpark,
  drawTarget,
  drawTrail,
  wingsAt,
} from './moustiques-swarm';

const KIND = 'moustiques';
const FORM_SPREAD = 240;
const MAX_HITS = 8;

interface Hit {
  readonly start: number;
  readonly x: number;
  readonly y: number;
}

interface Live {
  readonly swarm: Swarm;
  readonly hits: Hit[];
  spawnedTick: number;
  spawnX: number;
  spawnY: number;
  hitColor: number;
}

export function createMoustiques(
  ctx: RenderContext,
  poseOf: (id: EntityId) => EnemyPose | undefined,
): Family {
  const { layers } = ctx;
  const effect = ctx.specials.get(KIND);
  const reach = effect?.kind === 'sting' ? effect.radius : UNIT;
  const wings = paintMosquitoes();
  const back = layers.sky.addChildAt(new Container(), 0);
  const front = layers.sky.addChild(new Container());
  const ground = layers.dim.addChild(new Graphics());
  const fx = layers.fx.addChild(new Graphics());
  const lives = new Map<EntityId, Live>();
  const exits: Farewell[] = [];
  const pool = createSwarmPool(back, front, wings);

  function liveOf(id: EntityId, frame: Frame, at: { x: number; y: number }): Live {
    let live = lives.get(id);
    if (live === undefined) {
      live = {
        swarm: pool.take(),
        hits: [],
        spawnedTick: frame.now,
        spawnX: at.x,
        spawnY: at.y,
        hitColor: 0xffffff,
      };
      lives.set(id, live);
    }
    return live;
  }

  function draw(
    live: Live,
    enemy: SimState['enemies'][number],
    pose: EnemyPose,
    frame: Frame,
    feet: readonly { x: number; y: number }[],
  ): void {
    const { swarm, hits } = live;
    const { palette, calm } = frame;
    const beats = frame.now / frame.tempo.ticksPerBeat;
    const unit = pose.radius / UNIT;
    const { x, y } = pose.sprite.position;
    const shown = pose.sprite.alpha;
    const share = density(enemy.hp, enemy.maxHp);
    const form = (frame.now - live.spawnedTick) / frame.tempo.ticksPerBeat;
    swarm.orbiters.forEach((sprite, index) => {
      if (!alive(index, share)) {
        if (Number.isNaN(swarm.diedAt[index])) {
          swarm.diedAt[index] = beats;
          swarm.colors[index] = live.hitColor;
        }
        const burst = spark(beats - (swarm.diedAt[index] ?? beats));
        sprite.visible = calm && burst !== undefined;
        sprite.alpha = burst?.alpha ?? 0;
        if (!calm && burst !== undefined) {
          const [sx, sy] = [swarm.lastX[index] ?? x, swarm.lastY[index] ?? y];
          drawSpark(fx, sx, sy, unit, swarm.colors[index] ?? palette.badVibeRim, burst);
        }
        return;
      }
      swarm.diedAt[index] = Number.NaN;
      const spot = orbit(index, beats);
      const float = jitter(index, beats, calm ? FLOAT.calm : FLOAT.live);
      let px = x + (spot.x + float.x) * unit;
      let py = y + (spot.y + float.y) * unit;
      if (!calm) {
        for (const hit of hits) {
          const push = shove((px - hit.x) / unit, (py - hit.y) / unit, beats - hit.start);
          px += push.x * unit;
          py += push.y * unit;
        }
        for (const foot of feet) {
          const away = part((px - foot.x) / unit, (py - foot.y) / unit);
          px += away.x * unit;
          py += away.y * unit;
        }
      }
      if (form < FORM_BEATS) {
        const p = forming(index, swarm.orbiters.length, form);
        if (p <= 0) {
          sprite.visible = false;
          return;
        }
        const sx = live.spawnX + (hash(index, 11) - 0.5) * FORM_SPREAD;
        const sy = live.spawnY + (hash(index, 12) - 0.5) * FORM_SPREAD;
        px = lerp(sx, px, easeOutCubic(p)) + formSway(p, index) * unit;
        py = lerp(sy, py, easeOutCubic(p));
      }
      keepIn(spot.front ? front : back, sprite);
      sprite.visible = true;
      sprite.texture = wingsAt(wings, index, beats, calm);
      setTint(sprite, palette.badVibeRim);
      sprite.position.set(px, py);
      sprite.scale.set((SIZE * unit) / FINE);
      sprite.alpha = shown;
      swarm.lastX[index] = px;
      swarm.lastY[index] = py;
    });
    const { sting } = enemy;
    if (sting === undefined) {
      hide(...swarm.trail);
    } else {
      drawTarget(ground, sting, reach, frame);
      const [tipX, tipY] = NEEDLE_TIP;
      const tip = { x: x + tipX * unit, y: y + tipY * unit };
      drawTrail(fx, swarm, sting, tip, unit, shown, frame, wings);
    }
    for (let index = hits.length - 1; index >= 0; index -= 1) {
      if (beats - (hits[index]?.start ?? beats) > SHOVE.beats) {
        hits.splice(index, 1);
      }
    }
  }

  return {
    update(state: SimState, alpha: number, frame: Frame): void {
      fx.clear();
      ground.clear();
      const feet = state.players
        .filter((player) => !player.downed)
        .map((player) => ({
          x: lerp(player.prevX, player.x, alpha),
          y: lerp(player.prevY, player.y, alpha),
        }));
      const seen = new Set<EntityId>();
      for (const enemy of state.enemies) {
        const pose = enemy.kind === KIND ? poseOf(enemy.id) : undefined;
        if (pose === undefined) {
          continue;
        }
        seen.add(enemy.id);
        draw(liveOf(enemy.id, frame, pose), enemy, pose, frame, feet);
      }
      for (const [id, live] of lives) {
        if (!seen.has(id)) {
          pool.release(live.swarm);
          lives.delete(id);
        }
      }
      for (let index = exits.length - 1; index >= 0; index -= 1) {
        const exit = exits[index];
        if (exit !== undefined && !drawFarewell(exit, frame, fx, { back, front }, wings)) {
          pool.release(exit.swarm);
          exits.splice(index, 1);
        }
      }
    },
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (event.type === 'enemySpawned' && event.kind === KIND) {
        const live = liveOf(event.id, frame, event);
        live.spawnedTick = frame.now;
        live.spawnX = event.x;
        live.spawnY = event.y;
      } else if (event.type === 'enemyHit' && lives.has(event.id)) {
        const live = lives.get(event.id);
        const pose = poseOf(event.id);
        if (live === undefined || pose === undefined) {
          return;
        }
        const dx = (event.fromX ?? pose.x + 1) - pose.x;
        const dy = (event.fromY ?? pose.y) - pose.y;
        const far = Math.hypot(dx, dy) || 1;
        if (live.hits.length >= MAX_HITS) {
          live.hits.shift();
        }
        const toward = pose.radius * 1.6;
        live.hits.push({
          start: frame.now / frame.tempo.ticksPerBeat,
          x: pose.sprite.position.x + (dx / far) * toward,
          y: pose.sprite.position.y + (dy / far) * toward,
        });
        const hitter = event.by === undefined ? undefined : byId(state.players, event.by);
        const token = hitter === undefined ? undefined : ctx.classTokens.get(hitter.classId);
        live.hitColor = token === undefined ? frame.palette.or : frame.palette[token];
      } else if (event.type === 'enemyDied' && event.kind === KIND) {
        const live = lives.get(event.id);
        if (live === undefined) {
          return;
        }
        lives.delete(event.id);
        const pose = poseOf(event.id);
        const radius = pose?.radius ?? ctx.enemyRadii?.get(KIND) ?? UNIT;
        exits.push({
          swarm: live.swarm,
          start: frame.show,
          x: pose?.sprite.position.x ?? event.x,
          y: pose?.sprite.position.y ?? event.y,
          unit: radius / UNIT,
          share: density(0, 1),
        });
      }
    },
    reset(): void {
      for (const live of lives.values()) {
        pool.release(live.swarm);
      }
      lives.clear();
      for (const exit of exits) {
        pool.release(exit.swarm);
      }
      exits.length = 0;
      fx.clear();
      ground.clear();
    },
    destroy(): void {
      back.destroy({ children: true });
      front.destroy({ children: true });
      ground.destroy();
      fx.destroy();
      for (const shape of wings) {
        shape.texture.destroy(true);
      }
    },
  };
}
